import React from 'react';
import { Jumbotron, Container } from 'react-bootstrap';

const ViewSupport = () => {
  return (
    <div className="viewPage Hero-banner">
      <Jumbotron className="support-header">
        <h1>MyLifeClock Support</h1>
        <p>We’re here to help you get the most out of MyLifeClock.</p>
      </Jumbotron>

      <Container className="support-container">
        <section>
          <h2>1. Getting Help</h2>
          <p>
            If you have questions about using MyLifeClock, encounter a problem,
            or would like to share feedback, you can reach us directly at:
          </p>
          <p>
            <strong>Email:</strong> support@mylifeclock.io
            <br />
            <strong>Website:</strong> https://www.mylifeclock.io
          </p>
          <p>
            We aim to respond to most inquiries within 2–3 business days.
            Response times may vary based on volume and complexity.
          </p>
        </section>

        <section>
          <h2>2. Common Questions</h2>

          <h3>2.1 Do I need an account to use the app?</h3>
          <p>
            No. On the MyLifeClock iOS app, you can use the core features
            without creating an account. You can enter your birth date and life
            expectancy directly in the app and view your life clock and iOS
            Home Screen widget using only data stored on your device.
          </p>
          <p>
            Creating an account is optional and is currently used if you also
            access MyLifeClock from the web app. If you choose to create an
            account, make sure you use the same email address on both web and
            mobile to keep your data in sync.
          </p>

          <h3>2.2 How do I update my date of birth or life expectancy?</h3>
          <p>
            In the iOS app, go to the <strong>Summary</strong> tab. If you are
            using MyLifeClock without an account, you can adjust your birth
            date and life expectancy directly on this screen and tap&nbsp;
            <strong>Save Life Clock Settings</strong>. These changes are stored
            on your device and are used by the app and widget.
          </p>
          <p>
            If you are signed in with an account and using the web app, you may
            also update your birth date and life expectancy on the MyLifeClock
            website. Changes made on the web will sync to the iOS app the next
            time your data is refreshed.
          </p>

          <h3>2.3 My widget is not updating. What can I do?</h3>
          <p>
            If your iOS widget does not appear to update:
          </p>
          <ul>
            <li>Open the MyLifeClock app to ensure your latest settings are saved.</li>
            <li>
              Confirm that your birth date and life expectancy are set correctly
              on the Summary screen.
            </li>
            <li>
              Remove and re-add the widget to your Home Screen, if necessary.
            </li>
            <li>
              If you are using an account with the web app, make sure you have a
              stable internet connection so updated values can sync.
            </li>
          </ul>
        </section>

        <section>
          <h2>3. Reporting Issues or Bugs</h2>
          <p>
            If you encounter an error, crash, or unexpected behavior, please
            email us at <strong>support@mylifeclock.io</strong> with:
          </p>
          <ul>
            <li>A brief description of the problem.</li>
            <li>The device model and iOS version you are using.</li>
            <li>
              Steps to reproduce the issue, if possible (for example: “Open the
              app, go to the Summary tab, change life expectancy, then…”).
            </li>
            <li>Any relevant screenshots (optional but helpful).</li>
          </ul>
          <p>
            This information helps us diagnose and fix the issue more quickly.
          </p>
        </section>

        <section>
          <h2>4. Account Management</h2>

          <h3>4.1 Resetting your password</h3>
          <p>
            The current version of the MyLifeClock iOS app does not include an
            in-app “Forgot password?” option. If you forget your password for a
            MyLifeClock account, please contact us at{' '}
            <strong>support@mylifeclock.io</strong> from the email address
            associated with your account, and we can assist you with resetting
            your password.
          </p>

          <h3>4.2 Updating your email address</h3>
          <p>
            If you need to change the email address associated with your
            MyLifeClock account, please contact support with your current email
            and the new email you would like to use. We may ask for additional
            information to verify ownership of the account.
          </p>

          <h3>4.3 Deleting your account</h3>
          <p>
            If you would like to delete your account and associated personal
            data, email us at <strong>support@mylifeclock.io</strong> from the
            email address linked to your account. After processing, your
            account, life timeline, and associated data will be removed, subject
            to any legal retention requirements.
          </p>
        </section>

        <section>
          <h2>5. Billing & Subscriptions</h2>
          <p>
            The current version of the MyLifeClock iOS app does not offer paid
            subscriptions. All available features in this version are provided
            at no charge.
          </p>
          <p>
            If we introduce optional paid subscriptions in the future, they will
            be managed through your Apple ID and the Apple App Store, and this
            page will be updated with details on how billing and cancellation
            work.
          </p>
        </section>

        <section>
          <h2>6. Feedback and Feature Requests</h2>
          <p>
            We welcome feedback and ideas for improving MyLifeClock. If you have
            suggestions for new features, design improvements, or usability
            changes, please email us at{' '}
            <strong>support@mylifeclock.io</strong> with “Feedback” in the
            subject line.
          </p>
        </section>

        <section>
          <h2>7. Additional Resources</h2>
          <p>
            For information about how we handle your personal data, please see
            our{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            .
          </p>
        </section>
      </Container>

      <style jsx>{`
        .support-header {
          text-align: center;
          padding: 40px 20px;
        }

        .support-container {
          max-width: 900px;
          margin: 0 auto 80px auto;
          background-color: #fff;
          color: #222;
          padding: 40px 30px;
          border-radius: 12px;
          box-shadow: 0 2px 20px rgba(0, 0, 0, 0.08);
          text-align: left;
          line-height: 1.7;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
        }

        section {
          margin-bottom: 40px;
        }

        h1 {
          font-size: 2.4rem;
          font-weight: 700;
        }

        h2 {
          font-size: 1.4rem;
          margin-top: 30px;
          font-weight: 600;
          color: #222;
          border-bottom: 2px solid #eee;
          padding-bottom: 6px;
        }

        h3 {
          font-size: 1.1rem;
          margin-top: 18px;
          color: #333;
          font-weight: 500;
        }

        p {
          font-size: 1rem;
          margin-top: 10px;
          margin-bottom: 15px;
        }

        ul {
          margin: 10px 0 15px 25px;
          padding-left: 20px;
          list-style: disc;
        }

        li {
          margin-bottom: 10px;
          text-align: left;
          font-size: 1rem;
        }

        a {
          color: #007bff;
          text-decoration: none;
        }

        a:hover {
          text-decoration: none;
        }

        strong {
          font-weight: 600;
        }

        @media (max-width: 768px) {
          .support-container {
            padding: 20px;
          }

          h1 {
            font-size: 2rem;
          }

          h2 {
            font-size: 1.2rem;
          }

          p,
          li {
            font-size: 0.95rem;
          }
        }
      `}</style>
    </div>
  );
};

export default ViewSupport;